import { formatPlayTime, type PeriodComparison, type ReportType } from "../../hooks/useProgressReport";

export interface ComparisonSectionProps {
  comparison: PeriodComparison;
  reportType: ReportType;
}

interface ChangeIndicatorProps {
  label: string;
  current: number | string;
  change: number;
  format?: "number" | "time";
}

function ChangeIndicator({ label, current, change, format = "number" }: ChangeIndicatorProps) {
  const isPositive = change > 0;
  const isNegative = change < 0;
  const displayChange = format === "time"
    ? formatPlayTime(Math.abs(change))
    : Math.abs(change);

  return (
    <div className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">
      <span className="font-body text-neutral-600">{label}</span>
      <div className="flex items-center gap-3">
        <span className="font-display font-semibold text-neutral-800">
          {format === "time" && typeof current === "number"
            ? formatPlayTime(current)
            : current}
        </span>
        {change !== 0 && (
          <span
            className={`flex items-center gap-0.5 px-2 py-0.5 rounded-lg text-xs font-body font-medium ${
              isPositive
                ? "bg-meadow-100 text-meadow-700"
                : isNegative
                ? "bg-fairy-100 text-fairy-700"
                : "bg-neutral-100 text-neutral-600"
            }`}
          >
            <span>{isPositive ? "↑" : isNegative ? "↓" : "→"}</span>
            <span>{displayChange}</span>
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Comparison section showing change from previous period.
 * Features trend indicators with clear visual feedback.
 */
export function ComparisonSection({ comparison, reportType }: ComparisonSectionProps) {
  if (!comparison.hasPreviousPeriod || !comparison.changes || !comparison.previousPeriod) {
    return (
      <div className="space-y-4">
        <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
          <span className="text-xl">📈</span>
          vs. Last {reportType === "weekly" ? "Week" : "Month"}
        </h2>

        <div className="bg-neutral-50 rounded-xl p-5 border border-neutral-100 text-center">
          <span className="text-3xl mb-2 block opacity-60">📊</span>
          <p className="font-body text-neutral-500">
            No data from the previous {reportType === "weekly" ? "week" : "month"} to compare.
          </p>
          <p className="font-body text-sm text-neutral-400 mt-1">
            Check back next {reportType === "weekly" ? "week" : "month"} for trends!
          </p>
        </div>
      </div>
    );
  }

  const { changes, previousPeriod } = comparison;
  const currentTime = previousPeriod.totalTimePlayedSeconds + changes.timePlayedChange;
  const currentChallenges = previousPeriod.challengesCompleted + changes.challengesChange;
  const currentStars = previousPeriod.starsEarned + changes.starsChange;

  // Determine overall trend
  const improvements = [
    changes.timePlayedChange > 0,
    changes.challengesChange > 0,
    changes.starsChange > 0,
  ].filter(Boolean).length;

  const overallTrend = improvements >= 2 ? "up" : improvements === 0 ? "down" : "mixed";

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">📈</span>
        vs. Last {reportType === "weekly" ? "Week" : "Month"}
      </h2>

      <div className="bg-cloud rounded-xl border border-neutral-100 shadow-sm overflow-hidden print:shadow-none">
        {/* Trend summary */}
        <div
          className={`p-4 ${
            overallTrend === "up"
              ? "bg-gradient-to-r from-meadow-50 to-meadow-100/50"
              : overallTrend === "down"
              ? "bg-gradient-to-r from-fairy-50 to-fairy-100/50"
              : "bg-gradient-to-r from-sky-50 to-sky-100/50"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-2xl">
              {overallTrend === "up" ? "🚀" : overallTrend === "down" ? "💪" : "📊"}
            </span>
            <div>
              <p
                className={`font-display font-semibold ${
                  overallTrend === "up"
                    ? "text-meadow-700"
                    : overallTrend === "down"
                    ? "text-fairy-700"
                    : "text-sky-700"
                }`}
              >
                {overallTrend === "up"
                  ? "Great progress!"
                  : overallTrend === "down"
                  ? "Keep going!"
                  : "Steady progress"}
              </p>
              <p className="font-body text-sm text-neutral-600">
                {overallTrend === "up"
                  ? "Most metrics improved from last period."
                  : overallTrend === "down"
                  ? "A lighter period - that's okay!"
                  : "Some areas improved, some stayed steady."}
              </p>
            </div>
          </div>
        </div>

        {/* Comparison rows */}
        <div className="px-4">
          <ChangeIndicator
            label="Time Played"
            current={currentTime}
            change={changes.timePlayedChange}
            format="time"
          />
          <ChangeIndicator
            label="Challenges"
            current={currentChallenges}
            change={changes.challengesChange}
          />
          <ChangeIndicator
            label="Stars Earned"
            current={currentStars}
            change={changes.starsChange}
          />
        </div>
      </div>
    </div>
  );
}

export default ComparisonSection;
